import {
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IpoLanguageService,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';

import { LineString } from 'geojson';
import { Polyline } from 'leaflet';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Language } from '../../../../shared/language-chooser/language-chooser-entities';
import { DeliveryReportPdfService } from '../../../../shared/pdf-tools/pdf-report/delivery-report-pdf.service';
import {
  RP_SPAN_INFORMATION_OPENED,
  SPAN_EDIT_CLICKED,
  SPAN_FIELD_REPORT_OPENED,
} from '../../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../../shared/posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../../../../shared/self-inspection-reports/service/entities';
import { RolloutProgressReportsService } from '../../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { ToastsService } from '../../../../shared/toast/toast.service';
import { DefinitionsService } from '../../../service/definitions/definitions.service';
import { SelectSpanModeService } from '../../../service/map/span/select-span-mode/select-span-mode.service';
import { SpanService } from '../../../service/map/span/span.service';
import {
  ConstructionDone,
  ReportStatus,
  SpanProperties,
} from '../../../span-progress-entities';
import { formatSpanLength } from '../../../utils/span-length.helper';
import {
  HighlightedFeatureInfo,
  MapInteractionService,
  OptionTypes,
} from '../../map-interaction/map-interaction.service';
import {
  createPopupPdfContext,
  PopupPdfDownloadController,
} from '../popup-pdf-download.controller';
import {
  buildRolloutReportUrl,
  cancelPdfDownloadOnPointChange,
  getIfsFromUrl,
  POPUP_PDF_DOWNLOAD_TOOLTIP,
  spanIdIdentityChanged,
} from '../popup-pdf.utils';

@Component({
  selector: 'app-span-popup-content',
  templateUrl: './span-popup-content.component.html',
  styleUrls: ['./span-popup-content.component.scss'],
  standalone: false,
})
export class SpanPopupContentComponent implements OnInit, OnChanges, OnDestroy {
  private readonly reportsV3Service = inject(RolloutProgressReportsService);
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  private readonly spanService = inject(SpanService);
  private readonly mapInteractionService = inject(MapInteractionService);

  @Input() properties: SpanProperties;
  @Output() dismiss = new EventEmitter();

  accommodationMethod: string;
  permissionType: string;
  language: Language;
  downloadTooltipLabel = POPUP_PDF_DOWNLOAD_TOOLTIP;
  pdfController: PopupPdfDownloadController;

  showExpandedSpanDetails = !!localStorage.getItem('showExpandedSpanDetails');

  v3SpanReport = computed(() => {
    return this.reportsV3Service.getStoredV3ReportsByMarkerType(
      RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
    )?.[this.properties.spanId];
  });

  private cancelSub$ = new Subject<void>();
  private destroyed = false;

  constructor(
    private languageService: IpoLanguageService,
    private definitionService: DefinitionsService,
    private changeDetector: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private readonly pdfService: DeliveryReportPdfService,
    private readonly toastsService: ToastsService,
    private readonly translate: IpoTranslatePipe,
    private readonly router: Router,
  ) {
    this.pdfController = new PopupPdfDownloadController(
      pdfService,
      toastsService,
      translate,
      createPopupPdfContext(
        () => {
          const ifsNumber = getIfsFromUrl(this.router.url);
          const deliveryOrderId = this.properties.spanId;
          if (!ifsNumber || !deliveryOrderId) return null;
          return {
            ifsNumber,
            deliveryOrderId,
            locale: this.language ?? 'en',
            address:
              this.properties.serialNumber ?? this.properties.spanId ?? '-',
            rolloutPointType: RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
          };
        },
        () => this.destroyed,
        () => this.changeDetector.detectChanges(),
      ),
    );
  }

  ngOnChanges(changes?: SimpleChanges): void {
    cancelPdfDownloadOnPointChange(changes, spanIdIdentityChanged, () =>
      this.pdfController.cleanup(),
    );
  }

  ngOnInit() {
    this.languageService
      .getLanguage$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((language) => {
        this.language = language as Language;
        this.accommodationMethod =
          this.definitionService.getAccommodationMethodTranslation(
            this.properties.accommodationMethodId,
            language,
          );
        this.permissionType =
          this.definitionService.getPermissionTypeTranslation(
            this.properties.permissionTypeId,
            language,
          );
        this.changeDetector.detectChanges();
      });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.pdfController.cleanup();
    this.cancelSub$.next();
    this.cancelSub$.complete();
  }

  enterSelectSpanMode(isEditMode: boolean) {
    if (isEditMode) {
      this.analyticsService.logEvent(SPAN_EDIT_CLICKED);
    }

    const v3Report = this.v3SpanReport();

    if (v3Report?.id) {
      this.addMultiSpanIdsToSelectedLayers();
      this.reportsV3Service.setStoredReportId(v3Report.id);
    }

    this.selectSpanModeService.addSelectedSpanLayer(
      this.mapInteractionService.selectedLayer as Polyline,
    );

    this.selectSpanModeService.setIsEditMode(!!this.v3SpanReport());
    this.selectSpanModeService.setSelectSpanMode(true);
    this.mapInteractionService.initMultiSpan();
  }

  addMultiSpanIdsToSelectedLayers = () => {
    const linkedMultiSpanIds = this.spanService.getLinkedSpanIds(
      this.v3SpanReport(),
      this.properties.spanId,
    );
    this.addMultiReportSpanLayersToStoredSpanData(
      linkedMultiSpanIds,
      this.spanService.getLinkedSpanLayers(linkedMultiSpanIds),
    );
  };

  addMultiReportSpanLayersToStoredSpanData = (
    linkedMultiSpanIds: string[],
    linkedSpanLayers: Polyline[],
  ): void => {
    linkedSpanLayers.forEach((layer) =>
      this.selectSpanModeService.addSelectedSpanLayer(layer),
    );

    this.selectSpanModeService.setOriginalReportSpanIds(
      linkedMultiSpanIds
        ? [this.properties.spanId, ...linkedMultiSpanIds]
        : [this.properties.spanId],
    );

    const layerList = [
      this.mapInteractionService.selectedLayer as Polyline,
      ...linkedSpanLayers,
    ];

    const originalHighlightedLayers: HighlightedFeatureInfo[] = layerList.map(
      (layer) => ({
        layer: layer as Polyline<LineString>,
        options: OptionTypes.Span,
      }),
    );

    this.selectSpanModeService.setOriginalHighlightedLayers(
      originalHighlightedLayers,
    );
  };

  openSpanV2ReportPage = () => {
    window.open(
      buildRolloutReportUrl(
        this.router.url,
        RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
        this.properties.spanId,
      ),
      '_blank',
    );
    this.analyticsService.logEvent(SPAN_FIELD_REPORT_OPENED);
  };

  onDownloadPreviewPdf(event: MouseEvent): void {
    this.pdfController.onDownloadPreviewPdf(event);
  }

  onDownloadPreviewPdfKeyDown(event: KeyboardEvent): void {
    this.pdfController.onDownloadPreviewPdfKeyDown(event);
  }

  get status(): string {
    return this.existingSpan ? 'popup.existingSpan' : ReportStatus.Unknown;
  }

  get existingSpan() {
    return this.properties.lineStatusId === ConstructionDone;
  }
  get lengthWithZeroDecimals() {
    return formatSpanLength(this.properties.length);
  }

  toggleExpandableContent() {
    if (!this.showExpandedSpanDetails) {
      this.setLocalStorageExpandedDetails();
      this.analyticsService.logEvent(RP_SPAN_INFORMATION_OPENED);
    } else {
      this.clearLocalStorageExpandedDetails();
    }
    this.showExpandedSpanDetails = !this.showExpandedSpanDetails;
    this.changeDetector.detectChanges();
  }

  setLocalStorageExpandedDetails() {
    localStorage.setItem('showExpandedSpanDetails', 'true');
  }

  clearLocalStorageExpandedDetails() {
    localStorage.removeItem('showExpandedSpanDetails');
  }

  onClosePopup() {
    this.dismiss.emit();
  }
}
